import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash2, Edit3 } from "lucide-react";
import { norm } from "@/lib/normalize";

const empty = { name: "", gstNumber: "", email: "", phone: "", address: "" };

export default function Suppliers() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const isAdmin = norm(user.roleName) === "admin" || norm(user.roleName) === "receptionist";

  const load = async () => {
    try { setRows((await api.get("/suppliers")).data); } catch { toast.error("Failed"); }
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editId) await api.put(`/suppliers/${editId}`, form);
      else await api.post("/suppliers", form);
      toast.success("Saved"); setOpen(false); load();
    } catch { toast.error("Failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete supplier?")) return;
    try { await api.delete(`/suppliers/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-8" data-testid="suppliers-page">
      <div className="flex items-end justify-between gap-4">
        <div><div className="overline mb-2">Vendors</div><h1 className="font-heading text-4xl text-white">Suppliers</h1></div>
        {isAdmin && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button onClick={() => { setForm(empty); setEditId(null); }} data-testid="add-supplier-btn" className="gold-button px-5 py-2.5 rounded-full inline-flex items-center gap-2"><Plus className="w-4 h-4" /> Add Supplier</button>
            </DialogTrigger>
            <DialogContent className="bg-emerald-surface border-gold-subtle text-white max-w-lg">
              <DialogHeader><DialogTitle className="font-heading text-2xl">{editId ? "Edit" : "New"} Supplier</DialogTitle></DialogHeader>
              <form onSubmit={save} className="space-y-3">
                <div><Label>Name</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-emerald-elev border-gold-subtle mt-1" /></div>
                <div><Label>GST Number</Label><Input value={form.gstNumber} onChange={(e) => setForm({ ...form, gstNumber: e.target.value })} className="bg-emerald-elev border-gold-subtle mt-1" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-emerald-elev border-gold-subtle mt-1" /></div>
                  <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-emerald-elev border-gold-subtle mt-1" /></div>
                </div>
                <div><Label>Address</Label><Textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="bg-emerald-elev border-gold-subtle mt-1" /></div>
                <button type="submit" className="gold-button w-full h-11 rounded-md">Save</button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="bg-emerald-surface border border-gold-subtle rounded-2xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-gold-subtle hover:bg-transparent">
              <TableHead className="text-emerald-muted w-64">Name</TableHead>
              <TableHead className="text-emerald-muted">GST Number</TableHead>
              <TableHead className="text-emerald-muted">Phone</TableHead>
              <TableHead className="text-emerald-muted">Address</TableHead>
              {isAdmin && <TableHead className="text-emerald-muted w-24"></TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((s) => (
              <TableRow key={s.id} className="border-gold-subtle" data-testid={`supplier-row-${s.id}`}>
                <TableCell className="text-white">{s.name}</TableCell>
                <TableCell className="text-emerald-muted">{s.gstNumber}</TableCell>
                <TableCell className="text-emerald-muted">{s.phone}</TableCell>
                <TableCell className="text-emerald-muted">{s.address}</TableCell>
                {isAdmin && (
                  <TableCell className="text-right">
                    <button onClick={() => { setForm({ name: s.name, gstNumber: s.gstNumber || "", email: s.email || "", phone: s.phone || "", address: s.address || "" }); setEditId(s.id); setOpen(true); }} className="text-emerald-muted hover:text-gold p-1"><Edit3 className="w-4 h-4" /></button>
                    <button onClick={() => del(s.id)} className="text-emerald-muted hover:text-red-300 p-1 ml-2" data-testid={`delete-supplier-${s.id}`}><Trash2 className="w-4 h-4" /></button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
